import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Icon, Switch, Tooltip } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';

import {
  useFDA2DRestrictions,
  useFDA3DGPRestrictions,
} from '@/features/fdaRestrictions';

import { useCheckReportSignature, useReportSettings } from '../../hooks';

import styles from './ReportSettingsToggle.module.scss';

type ReportSettingsToggleProps = {
  className?: string;
};

export const ReportSettingsToggle: FC<ReportSettingsToggleProps> = (props) => {
  const { className } = props;

  const { formatMessage } = useIntl();
  const { isLoading, settings, updateSettings } = useReportSettings();
  const { isMobile } = useMedia();

  const hideProbability = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_Probabilities,
    ),
  );

  const {
    shouldHideSuspiciousTeethToggle: shouldHideSuspiciousTeethToggle3D,
    shouldHideConditionDetailsToggle: shouldHideConditionDetailsToggle3D,
  } = useFDA3DGPRestrictions();

  const {
    shouldHideSuspiciousTeethToggle: shouldHideSuspiciousTeethToggle2D,
    shouldHideConditionDetailsToggle: shouldHideConditionDetailsToggle2D,
  } = useFDA2DRestrictions();

  const shouldHideSuspiciousTeethToggle =
    shouldHideSuspiciousTeethToggle3D || shouldHideSuspiciousTeethToggle2D;
  const shouldHideConditionDetailsToggle =
    shouldHideConditionDetailsToggle3D || shouldHideConditionDetailsToggle2D;

  const { checkReportSignature } = useCheckReportSignature();

  const isLowProbabilityModeEnabled = settings.LowProbabilityMode;
  const isShowConditionDetailsEnabled = settings.ShowConditionDetails;

  const lowProbabilityModeToggle = () => {
    if (!isLowProbabilityModeEnabled) {
      trackEvent({
        eventName: 'ReportPageFooterSuspiciousTeethToggleClicked',
      });
    }

    checkReportSignature({
      onSignatureChecked: async () =>
        updateSettings({
          LowProbabilityMode: !isLowProbabilityModeEnabled,
        }),
    });
  };

  const toggleConditionDetails = () => {
    if (!isShowConditionDetailsEnabled) {
      trackEvent({
        eventName: 'ReportPageFooterConditionDetailsToggleClicked',
      });
    }

    checkReportSignature({
      onSignatureChecked: async () =>
        updateSettings({
          ShowConditionDetails: !isShowConditionDetailsEnabled,
        }),
    });
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-section="report-settings-toggles"
    >
      {!hideProbability && !shouldHideSuspiciousTeethToggle && (
        <div className={styles.lowProbability}>
          <Switch
            checked={isLowProbabilityModeEnabled}
            disabled={isLoading}
            onChange={lowProbabilityModeToggle}
            labelClassName={cn(isMobile && 'p2m')}
          >
            <FormattedMessage
              id="reportSettings.showLowProbability"
              defaultMessage="Suspicious teeth"
            />
          </Switch>

          {!isMobile && (
            <Tooltip.Primary
              content={formatMessage({
                id: 'lowProbabilityInfo',
                defaultMessage:
                  'Switch on to see caries signs and periradicular pathologies, with probability rate 30-50%',
              })}
            >
              <div className={styles.lowProbabilityInfoIcon}>
                <Icon name="info" size={24} />
              </div>
            </Tooltip.Primary>
          )}
        </div>
      )}

      {!shouldHideConditionDetailsToggle && (
        <div className={styles.diagnosisDetails}>
          <Switch
            checked={isShowConditionDetailsEnabled}
            disabled={isLoading}
            onChange={toggleConditionDetails}
            labelClassName={cn(isMobile && 'p2m')}
          >
            <FormattedMessage
              id="reportSettings.showConditionDetails"
              defaultMessage="Conditions details"
            />
          </Switch>

          {!isMobile && (
            <Tooltip.Primary
              content={formatMessage({
                id: 'showConditionDetailsInfo',
                defaultMessage:
                  'Turn on to view all detected conditions with details',
              })}
            >
              <div className={styles.lowProbabilityInfoIcon}>
                <Icon name="info" size={24} />
              </div>
            </Tooltip.Primary>
          )}
        </div>
      )}
    </div>
  );
};
